"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
function handleEvent(event, filters, handler) {
    for (var _i = 0, filters_1 = filters; _i < filters_1.length; _i++) {
        var filter = filters_1[_i];
        if (!filter(event)) {
            return;
        }
    }
    if (handler) {
        handler(event);
    }
}
var keyCodes = {
    esc: 27,
    tab: 9,
    enter: 13,
    space: 32,
    up: 38,
    down: 40,
    del: [8, 46],
    left: 37,
    right: 39
};
function createKeyFilter(keys) {
    var codes = [];
    for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
        var key = keys_1[_i];
        if (typeof key === "number") {
            codes.push(key);
        }
        else {
            var code = keyCodes[key];
            if (typeof code === "number") {
                codes.push(code);
            }
            else {
                codes.push.apply(codes, code);
            }
        }
    }
    switch (codes.length) {
        case 0:
            return function (_) { return false; };
        case 1:
            var code_1 = codes[0];
            return function (e) { return e.keyCode === code_1; };
        default:
            return function (e) { return codes.indexOf(e.keyCode) >= 0; };
    }
}
function defineChildModifier(target, currentFilters, name, filter, children) {
    Object.defineProperty(target, name, {
        get: function () {
            // call this getter at most once.
            // reuse created instance after next time.
            var ret = createModifier(currentFilters.concat([filter]), children);
            Object.defineProperty(target, name, {
                value: ret,
                enumerable: true
            });
            return ret;
        },
        enumerable: true,
        configurable: true
    });
}
function defineKeyCodeModifiers(target, filters, children) {
    var _loop_1 = function (name_1) {
        var keyName = name_1;
        if (keyName === "left" || keyName === "right") {
            return "continue";
        }
        var code = keyCodes[keyName];
        if (typeof code === "number") {
            defineChildModifier(target, filters, keyName, function (e) { return e.keyCode === code; }, children);
        }
        else {
            var c1_1 = code[0], c2_1 = code[1];
            defineChildModifier(target, filters, keyName, function (e) { return e.keyCode === c1_1 || e.keyCode === c2_1; }, children);
        }
    };
    for (var name_1 in keyCodes) {
        _loop_1(name_1);
    }
}
function defineKeys(target, filters, children) {
    Object.defineProperty(target, "keys", {
        get: function () {
            var _this = this;
            var keysFunction = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var propName = "keys:" + args.toString();
                var modifier = _this[propName];
                if (modifier !== undefined) {
                    return modifier;
                }
                var filter = createKeyFilter(args);
                defineChildModifier(_this, filters, propName, filter, children);
                return _this[propName];
            };
            Object.defineProperty(this, "keys", {
                value: keysFunction,
                enumerable: true
            });
            return keysFunction;
        },
        enumerable: true,
        configurable: true
    });
}
function defineExact(target, filters, children) {
    Object.defineProperty(target, "exact", {
        get: function () {
            var _this = this;
            var exactFunction = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var propName = "exact:" + args.toString();
                var modifier = _this[propName];
                if (modifier !== undefined) {
                    return modifier;
                }
                var expected = {
                    ctrl: false,
                    shift: false,
                    alt: false,
                    meta: false
                };
                args.forEach(function (arg) { return (expected[arg] = true); });
                var filter = function (e) {
                    return !!e.ctrlKey === expected.ctrl &&
                        !!e.shiftKey === expected.shift &&
                        !!e.altKey === expected.alt &&
                        !!e.metaKey === expected.meta;
                };
                defineChildModifier(_this, filters, propName, filter, children);
                return _this[propName];
            };
            Object.defineProperty(this, "exact", {
                value: exactFunction,
                enumerable: true
            });
            return exactFunction;
        },
        enumerable: true,
        configurable: true
    });
}
function createModifier(filters, children) {
    function m(arg) {
        if (arg instanceof Function) {
            // EventHandler => EventHandler
            return function (event) { return handleEvent(event, filters, arg); };
        }
        else {
            // Event => void
            handleEvent(arg, filters);
            return;
        }
    }
    if (children.keyboard || children.mouse) {
        var nextChildren = __assign({}, children, { keyboard: false, mouse: false });
        if (children.keyboard) {
            defineKeyCodeModifiers(m, filters, nextChildren);
            defineKeys(m, filters, nextChildren);
        }
        if (children.mouse) {
            defineChildModifier(m, filters, "middle", function (e) { return e.button === 1; }, nextChildren);
        }
        defineChildModifier(m, filters, "left", function (e) { return e.keyCode === 37 || e.button === 0; }, nextChildren);
        defineChildModifier(m, filters, "right", function (e) { return e.keyCode === 39 || e.button === 2; }, nextChildren);
    }
    if (children.exact) {
        var nextChildren = __assign({}, children, { exact: false, modkey: false });
        defineExact(m, filters, nextChildren);
    }
    if (children.modkey) {
        var nextChildren = __assign({}, children, { exact: false });
        defineChildModifier(m, filters, "ctrl", function (e) { return e.ctrlKey; }, nextChildren);
        defineChildModifier(m, filters, "shift", function (e) { return e.shiftKey; }, nextChildren);
        defineChildModifier(m, filters, "alt", function (e) { return e.altKey; }, nextChildren);
        defineChildModifier(m, filters, "meta", function (e) { return e.metaKey; }, nextChildren);
        defineChildModifier(m, filters, "noctrl", function (e) { return !e.ctrlKey; }, nextChildren);
        defineChildModifier(m, filters, "noshift", function (e) { return !e.shiftKey; }, nextChildren);
        defineChildModifier(m, filters, "noalt", function (e) { return !e.altKey; }, nextChildren);
        defineChildModifier(m, filters, "nometa", function (e) { return !e.metaKey; }, nextChildren);
    }
    defineChildModifier(m, filters, "stop", function (e) {
        e.stopPropagation();
        return true;
    }, children);
    defineChildModifier(m, filters, "prevent", function (e) {
        e.preventDefault();
        return true;
    }, children);
    defineChildModifier(m, filters, "self", function (e) { return e.target === e.currentTarget; }, children);
    return m;
}
exports.modifiers = createModifier([], {
    keyboard: true,
    mouse: true,
    modkey: true,
    exact: true
});
//# sourceMappingURL=data:application/json;base64,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